import { CommentModel } from '@barthauer/abc/common-editors';
import { CollectionHelper, CompareHelper, ConvertHelper, DataUrl, GUID } from '@barthauer/abc/core';
import { IPersistable, ObjectSpaceService, PersistenceService } from '@barthauer/abc/core/data';
import { MtDocument, Reminder } from '../models/mtData.model';
import { MtTicket, MtTicketTask } from '../models/mtData.tickets.model';
import { TicketConstants, TicketState, TicketTaskState } from '../tickets/tickets.model';
import { IDrawable, ISelfDescribable, MobileObjectViewModel } from './mobileData.viewModel';
import { MTHelper } from '../shared/helpers';
import { MobileDocumentViewModel, MobileReminderViewModel } from './mobileData.common.viewModel';
import { MobileResourceViewModel } from './mobileData.resources.viewModel';

export interface IMobileTicket extends ISelfDescribable {
  //number: string;
}

export class MobileTicketViewModel implements IMobileTicket, IPersistable, IDrawable {
  private _ticketTasks: MobileTicketTaskViewModel[];
  private _object: MobileObjectViewModel;
  private persistenceService: PersistenceService;
  private mtTicket: MtTicket;
  private _documents: MobileDocumentViewModel[];
  private resources: Map<string, MobileResourceViewModel>;

  get id(): string {
    return this.mtTicket.Id;
  }
  get number(): string {
    return this.mtTicket.Nummer;
  }
  get object(): MobileObjectViewModel {
    return this._object;
  }
  get state(): string {
    return this.mtTicket.Status ?? this.getTicketStateFromTicketTasks();
  }
  set state(value: string) {
    this.mtTicket.Status = value;
  }
  get filterableObjectId(): string {
    return this.drawableObjectId;
  }
  get filterableObjectType(): number | string {
    return this.drawableObjectType;
  }

  get drawableObjectId(): string {
    return this.object?.drawableObjectId ?? this.id;
  }
  get drawableObjectType(): number | string {
    return this.object?.drawableObjectType ?? this.objectType;
  }

  public getTicketStateFromTicketTasks(): TicketState {
    let retState: TicketState = TicketState.Offen;
    if (this.ticketTasks.every((u) => u.state === TicketTaskState.Erledigt)) {
      retState = TicketState.Erledigt;
    } else if (
      this.ticketTasks.some((u) => u.state === TicketTaskState.InBearbeitung || u.state === TicketTaskState.Erledigt)
    ) {
      retState = TicketState.InBearbeitung;
    }
    return retState;
  }

  refreshState(): void {
    const newState: TicketState = this.getTicketStateFromTicketTasks();
    if (this.mtTicket.Status !== newState) {
      this.persistenceService.addObjectValue(this, 'Status', newState, this.mtTicket.Status);
    }
    this.state = newState;
  }

  get priority(): string {
    return this.mtTicket.Dringlichkeit;
  }
  get priorityDisplayText(): string {
    return this.mtTicket.DringlichkeitDisplayText;
  }

  get designation(): string {
    return this.mtTicket.Bezeichnung;
  }

  get objectDesignation(): string {
    return this.object?.designation;
  }

  get objectObjectType(): string {
    return this.object?.type;
  }
  get street(): string {
    return this.object?.street;
  }

  get headerTypeDisplayText(): string {
    if (this.objectObjectType) {
      return this.objectObjectType + ':';
    } else {
      return '';
    }
  }

  get stateIconName(): string {
    switch (this.state) {
      case TicketState.InBearbeitung:
        return 'StatePartial-Image';
      case TicketState.Erledigt:
        return 'StateDone-Image';
      case TicketState.Offen:
        return 'StateOpen-Image';
    }
    return null;
  }

  hasState(state: TicketState) {
    return this.state === state;
  }

  get hasGeometry(): boolean {
    return this.mtTicket?.HasGeometry;
  }

  get ticketTasks(): MobileTicketTaskViewModel[] {
    if (!this._ticketTasks) {
      this._ticketTasks = [];
      for (const task of this.mtTicket.TicketTasks) {
        const t: MobileTicketTaskViewModel = new MobileTicketTaskViewModel();
        t.load(this.persistenceService, task, this, this.resources.get(task.IdRefMtResource));
        this._ticketTasks.push(t);
      }
    }
    return this._ticketTasks;
  }

  get documents(): MobileDocumentViewModel[] {
    if (!this._documents) {
      this._documents = [];
    }
    return this._documents;
  }

  get hasActiveReminders(): boolean {
    return this.ticketTasks.some((tt) => tt.hasActiveReminders);
  }

  get allowDeletion(): boolean {
    return this.mtTicket.AllowDeletion;
  }

  constructor() {}
  get objectType(): string {
    return TicketConstants.ObjectTypeName_MtTicket;
  }

  get persistenceCategory(): string {
    return TicketConstants.PersistenceCategory_TicketDetail;
  }

  async load(
    persistenceService: PersistenceService,
    objectSpaceService: ObjectSpaceService,
    mtTicket: MtTicket,
    resources: MobileResourceViewModel[],
  ): Promise<void> {
    this.persistenceService = persistenceService;
    this.mtTicket = mtTicket;
    this.resources = new Map<string, MobileResourceViewModel>(resources.map((r) => [r.id, r]) ?? []);
    this._object = new MobileObjectViewModel(mtTicket.Object);
    await this._object.calcMainType(objectSpaceService);
  }
  getValues(): string[] {
    const values: string[] = [];
    CollectionHelper.safePushValueToArray<string>(values, this.designation);
    CollectionHelper.safePushValueToArray<string>(values, this.priorityDisplayText);
    CollectionHelper.safePushValueToArray<string>(values, this.objectDesignation);
    CollectionHelper.safePushValueToArray<string>(values, this.objectObjectType);
    CollectionHelper.safePushValueToArray<string>(values, this.street);
    CollectionHelper.safePushValueToArray<string>(values, this.number);
    return values;
  }

  remove() {
    this.persistenceService.addObjectForDelete(this);
  }

  public addDocumentByDataUrl(designation: string, date: Date, dataUrl: DataUrl): MobileDocumentViewModel {
    const newDocVM: MobileDocumentViewModel = new MobileDocumentViewModel();
    newDocVM.createNewByDataUrl(this.persistenceService, this.persistenceCategory, this, designation, date, dataUrl);
    this.documents.push(newDocVM);
    return newDocVM;
  }

  public addDocumentByFile(designation: string, date: Date, file: File): MobileDocumentViewModel {
    const newDocVM: MobileDocumentViewModel = new MobileDocumentViewModel();
    newDocVM.createNewByFile(this.persistenceService, this.persistenceCategory, this, designation, date, file);
    this.documents.push(newDocVM);
    return newDocVM;
  }

  public addTask(
    designation: string,
    lfdNr: number,
    status: string,
    startDatum: Date,
    startZeit: Date,
    endDatum: Date,
    endZeit: Date,
    idRefMtResource: string,
    createActiveReminder: boolean,
  ): MobileTicketTaskViewModel {
    const newTaskVM: MobileTicketTaskViewModel = new MobileTicketTaskViewModel();
    newTaskVM.createNew(
      this.persistenceService,
      this,
      designation,
      lfdNr,
      status,
      startDatum,
      startZeit,
      endDatum,
      endZeit,
      this.resources.get(idRefMtResource),
    );
    if (createActiveReminder) {
      newTaskVM.addReminder(true);
    }
    this.ticketTasks.push(newTaskVM);
    return newTaskVM;
  }

  toString(): string {
    let ret: string = '';
    if (this.number) {
      ret = this.number;
    }

    if (this.designation) {
      ret = ret += ', ' + this.designation;
    }

    if (this.priorityDisplayText) ret += ', ' + this.priorityDisplayText;

    if (this.object) {
      if (this.object?.street) ret += ', ' + this.object?.street;
      if (this.object?.designation)
        ret += ', ' + (this.objectObjectType ? this.objectObjectType + ' ' : '') + this.object?.designation;
    }

    return ret;
  }
}

export class MobileTicketTaskViewModel implements IPersistable {
  private persistenceService: PersistenceService;
  private mtTicketTask: MtTicketTask;
  private ticket: MobileTicketViewModel;
  private _reminders: MobileReminderViewModel[];
  public comments: CommentModel[];
  private _documents: MobileDocumentViewModel[];
  private _resource: MobileResourceViewModel;

  constructor() {}

  get parentTicket(): MobileTicketViewModel {
    return this.ticket;
  }

  get id(): string {
    return this.mtTicketTask.Id;
  }

  get objectType(): string {
    return TicketConstants.ObjectTypeName_MtTicketTask;
  }

  get objectTypeValue(): number {
    return this.mtTicketTask.ObjectType;
  }

  get persistenceCategory(): string {
    return TicketConstants.PersistenceCategory_TicketDetail;
  }

  revertChanges() {
    const pack = this.persistenceService.getPendingObjectPackage(this);
    if (pack && !CompareHelper.isEmptyObject(pack.fieldValueHistories)) {
      pack.setOriginalFieldHistoryValue('Status', (value) => (this.mtTicketTask.Status = value));
      pack.setOriginalFieldHistoryValue('EndDatum', (value) => (this.mtTicketTask.EndDatum = value));
      pack.setOriginalFieldHistoryValue('EndZeit', (value) => (this.mtTicketTask.EndZeit = value));
      pack.setOriginalFieldHistoryValue('StartDatum', (value) => (this.mtTicketTask.StartDatum = value));
      pack.setOriginalFieldHistoryValue('StartZeit', (value) => (this.mtTicketTask.StartZeit = value));
    }
    this.revertDocuments();
  }

  load(
    persistenceService: PersistenceService,
    mtTicketTask: MtTicketTask,
    ticket: MobileTicketViewModel,
    resource: MobileResourceViewModel,
  ): void {
    this.persistenceService = persistenceService;
    this.mtTicketTask = mtTicketTask;
    this.ticket = ticket;
    this.comments = mtTicketTask.Comments;
    this._resource = resource;
  }

  createNew(
    persistenceService: PersistenceService,
    ticket: MobileTicketViewModel,
    designation: string,
    lfdNr: number,
    status: string,
    startDatum: Date,
    startZeit: Date,
    endDatum: Date,
    endZeit: Date,
    resource: MobileResourceViewModel,
  ): MtTicketTask {
    const newTask: MtTicketTask = new MtTicketTask({
      Id: GUID.newGUID(),
      Bezeichnung: designation,
      LfdNr: lfdNr,
      Status: status,
      StartDatum: ConvertHelper.toISODateString(startDatum),
      StartZeit: ConvertHelper.toISOTimeString(startZeit),
      EndDatum: ConvertHelper.toISODateString(endDatum),
      EndZeit: ConvertHelper.toISOTimeString(endZeit),
      IdRefMtResource: resource.id,
    });

    this.load(persistenceService, newTask, ticket, resource);

    this.persistenceService.addObjectForInsert(this);
    this.persistenceService.addObjectValue(this, 'ParentId', ticket.id);
    this.persistenceService.addObjectValue(this, 'Bezeichnung', designation);
    this.persistenceService.addObjectValue(this, 'LfdNr', lfdNr);
    this.persistenceService.addObjectValue(this, 'Status', status);
    this.persistenceService.addObjectValue(this, 'StartDatum', newTask.StartDatum);
    this.persistenceService.addObjectValue(this, 'StartZeit', newTask.StartZeit);
    this.persistenceService.addObjectValue(this, 'EndDatum', newTask.EndDatum);
    this.persistenceService.addObjectValue(this, 'EndZeit', newTask.EndZeit);
    this.persistenceService.addObjectValue(this, 'IdRefMtResource', resource.id);

    return newTask;
  }

  get designation(): string {
    return this.mtTicketTask.Bezeichnung;
  }

  get resource(): MobileResourceViewModel {
    return this._resource;
  }

  get state(): string {
    return this.mtTicketTask.Status;
  }

  set state(value: string) {
    if (this.mtTicketTask.Status !== value) {
      this.persistenceService.addObjectValue(this, 'Status', value, this.mtTicketTask.Status);
      this.mtTicketTask.Status = value;
      this.ticket.refreshState();

      if (value === TicketTaskState.Erledigt) {
        const curDateTime = new Date();
        this.endDate = curDateTime;
        this.endTime = curDateTime;
      }
    }
  }

  get documents(): MobileDocumentViewModel[] {
    if (!this._documents) {
      this._documents = [];
      for (const document of this.mtTicketTask.Documents) {
        const d: MobileDocumentViewModel = new MobileDocumentViewModel();
        d.load(this.persistenceService, document, this.persistenceCategory, true);
        this._documents.push(d);
      }
      MTHelper.sortMobileDocuments(this._documents);
    }
    return this._documents;
  }

  get reminders(): MobileReminderViewModel[] {
    if (!this._reminders) {
      this._reminders = [];
      for (const reminder of this.mtTicketTask.Reminders) {
        const d: MobileReminderViewModel = new MobileReminderViewModel();
        d.load(this.persistenceService, reminder, this.persistenceCategory);
        this._reminders.push(d);
      }
    }
    return this._reminders;
  }

  public addReminder(isActive: boolean): MobileReminderViewModel {
    const newRemVM: MobileReminderViewModel = new MobileReminderViewModel();
    const newRem: Reminder = newRemVM.createNew(this.persistenceService, this.persistenceCategory, this, isActive);
    this.mtTicketTask.Reminders.push(newRem);
    this.reminders.push(newRemVM);
    return newRemVM;
  }

  public addDocumentByDataUrl(designation: string, date: Date, dataUrl: DataUrl): MobileDocumentViewModel {
    const newDocVM: MobileDocumentViewModel = new MobileDocumentViewModel();
    const newMtDoc: MtDocument = newDocVM.createNewByDataUrl(
      this.persistenceService,
      this.persistenceCategory,
      this,
      designation,
      date,
      dataUrl,
    );
    this.mtTicketTask.Documents.push(newMtDoc);
    this.documents.push(newDocVM);
    return newDocVM;
  }

  public addDocumentByFile(designation: string, date: Date, file: File): MobileDocumentViewModel {
    const newDocVM: MobileDocumentViewModel = new MobileDocumentViewModel();
    const newMtDoc: MtDocument = newDocVM.createNewByFile(
      this.persistenceService,
      this.persistenceCategory,
      this,
      designation,
      date,
      file,
    );
    this.mtTicketTask.Documents.push(newMtDoc);
    this.documents.push(newDocVM);
    return newDocVM;
  }

  public revertDocuments(): void {
    if (this._documents) {
      const nonPersistedDocs = this._documents.filter((d) => !d.isPersisted);
      this._documents = this._documents.filter((d) => nonPersistedDocs.indexOf(d) === -1);

      this.mtTicketTask.Documents = this.mtTicketTask.Documents.filter(
        (d) => nonPersistedDocs.map((d2) => d2.mtDocument).indexOf(d) === -1,
      );
    }
  }

  get hasActiveReminders(): boolean {
    return this.reminders.some((rem) => rem.isActive);
  }

  get startDate(): Date {
    return this.getDateFromString(this.mtTicketTask.StartDatum);
  }

  set startDate(val: Date) {
    if (val) {
      const strDate = ConvertHelper.toISODateString(val);
      this.persistenceService.addObjectValue(this, 'StartDatum', strDate, this.mtTicketTask.StartDatum);
      this.mtTicketTask.StartDatum = strDate;
    }
  }

  get startTime(): Date {
    return this.getTimeFromString(this.mtTicketTask.StartZeit);
  }

  set startTime(val: Date) {
    if (val) {
      if (this.timeStringChanged(val, this.mtTicketTask.StartZeit)) {
        const strTime = this.ensureValidIsoString(val);
        this.persistenceService.addObjectValue(this, 'StartZeit', strTime, this.mtTicketTask.StartZeit);
        this.mtTicketTask.StartZeit = strTime;
      }
    }
  }

  get endDate(): Date {
    return this.getDateFromString(this.mtTicketTask.EndDatum);
  }

  set endDate(val: Date) {
    if (val) {
      const strDate = ConvertHelper.toISODateString(val);
      this.persistenceService.addObjectValue(this, 'EndDatum', strDate, this.mtTicketTask.EndDatum);
      this.mtTicketTask.EndDatum = strDate;
    }
  }

  get endTime(): Date {
    return this.getTimeFromString(this.mtTicketTask.EndZeit);
  }

  set endTime(val: Date) {
    if (val) {
      if (this.timeStringChanged(val, this.mtTicketTask.EndZeit)) {
        const endTime = this.ensureValidIsoString(val);
        this.persistenceService.addObjectValue(this, 'EndZeit', endTime, this.mtTicketTask.EndZeit);
        this.mtTicketTask.EndZeit = endTime;
      }
    }
  }

  private timeStringChanged(time: Date, previousTimeString: string): boolean {
    let retChanged: boolean = true;

    if (time && previousTimeString && this.isValidDate(time)) {
      const curTime: string = ConvertHelper.toISOTimeString(time);
      const prevTime: Date = this.getTimeFromString(previousTimeString);
      if (prevTime) {
        const prevIsoTimeString = ConvertHelper.toISOTimeString(prevTime);
        retChanged = curTime !== prevIsoTimeString;
      }
    }
    return retChanged;
  }

  private ensureValidIsoString(newDate: Date): string {
    let ret = null;
    if (!this.isValidDate(newDate)) newDate = new Date();
    ret = ConvertHelper.toISOTimeString(newDate);
    return ret;
  }

  private isValidDate(date: Date): boolean {
    return date instanceof Date && !isNaN(date.getTime());
  }

  getDateFromString(strDate: string): Date {
    let retDate: Date = null;
    if (strDate) retDate = new Date(strDate);
    return retDate;
  }

  getTimeFromString(strTime: string): Date {
    let retTime: Date = null;
    if (strTime) {
      retTime = new Date(strTime);
      if (!this.isValidDate(retTime)) {
        const currentDate = new Date().toISOString().split('T')[0];
        const dateTimeString = `${currentDate}T${strTime}`;
        const dateObject = new Date(dateTimeString);

        if (!isNaN(dateObject.getTime())) {
          retTime = dateObject;
        } else {
          console.log('Invalid Date by getting date from time string!');
        }
      }
    }

    return retTime;
  }
}
